<template>
  <div class="gasto-planificado-form">
    <div class="card">
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0">{{ editMode ? 'Editar Gasto Planificado' : 'Nuevo Gasto Planificado' }}</h5>
      </div>
      <div class="card-body">
        <form @submit.prevent="submitForm">
          <div class="mb-3">
            <label for="concepto" class="form-label">Concepto*</label>
            <input
              type="text"
              class="form-control"
              id="concepto"
              v-model="form.concepto"
              required
              placeholder="Ej: Compra de móvil"
            >
          </div>
          
          <div class="row mb-3">
            <div class="col-md-6">
              <label for="cantidad" class="form-label">Cantidad (€)*</label>
              <input
                type="number"
                class="form-control"
                id="cantidad"
                v-model="form.cantidad"
                step="0.01"
                min="0"
                required
                :disabled="editMode && form.pagos_realizados > 0"
                placeholder="Ej: 1200.00"
              >
            </div>
            <div class="col-md-6">
              <label for="tipo_division" class="form-label">División*</label>
              <select
                class="form-select"
                id="tipo_division"
                v-model="form.tipo_division"
                required
                :disabled="editMode && form.pagos_realizados > 0"
              >
                <option value="total">Total</option>
                <option value="mitad">Mitad</option>
              </select>
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label">Cantidad Total</label>
              <div class="form-control bg-light">{{ cantidadTotalMostrar }}</div>
              <small class="text-muted" v-if="form.tipo_division === 'mitad'">
                Se contabilizará la mitad del importe introducido.
              </small>
            </div>
            <div class="col-md-6">
              <label for="num_pagos" class="form-label">Número de Pagos*</label>
              <input
                type="number"
                class="form-control"
                id="num_pagos"
                v-model="form.num_pagos"
                min="1"
                required
                :disabled="editMode && form.pagos_realizados > 0"
                placeholder="Ej: 6 (1 = pago único)"
              >
              <small class="text-muted" v-if="cantidadPorPago > 0">
                {{ form.num_pagos }} pagos de {{ cantidadPorPago.toFixed(2) }}€
              </small>
            </div>
          </div>
          
          <div class="row mb-3">
            <div class="col-md-6">
              <div v-if="mostrarFechaInicio || (form.fecha_inicio && form.fecha_inicio !== '')">
                <label for="fecha_inicio" class="form-label">Fecha de Inicio</label>
                <div class="input-group">
                  <input
                    type="date"
                    class="form-control"
                    id="fecha_inicio"
                    v-model="form.fecha_inicio"
                    :disabled="editMode && form.pagos_realizados > 0"
                  >
                  <button 
                    type="button" 
                    class="btn btn-outline-secondary" 
                    @click="eliminarFechaInicio"
                    v-if="form.fecha_inicio && form.fecha_inicio !== ''"
                    :disabled="editMode && form.pagos_realizados > 0"
                  >
                    <i class="bi bi-x"></i>
                  </button>
                </div>
                <small class="text-muted">
                  Si no se especifica, los pagos no se programarán hasta que se establezca una fecha.
                </small>
              </div>
              <div v-else>
                <button 
                  type="button" 
                  class="btn btn-outline-primary mt-4" 
                  @click="mostrarFechaInicio = true"
                >
                  <i class="bi bi-calendar-plus"></i> Añadir fecha de inicio
                </button>
                <small class="d-block text-muted mt-2">
                  Si no se especifica, los pagos no se programarán hasta que se establezca una fecha.
                </small>
              </div>
            </div>
            <div class="col-md-6">
              <label for="periodicidad" class="form-label">Periodicidad*</label>
              <select
                class="form-select"
                id="periodicidad"
                v-model="form.periodicidad"
                required
                :disabled="editMode && form.pagos_realizados > 0"
              >
                <option value="">Seleccionar periodicidad</option>
                <option value="semanal">Semanal</option>
                <option value="quincenal">Quincenal</option>
                <option value="mensual">Mensual</option>
              </select>
            </div>
          </div>
          
          <div class="mb-3">
            <label for="categoria_id" class="form-label">Categoría</label>
            <select
              class="form-select"
              id="categoria_id"
              v-model="form.categoria_id"
            >
              <option value="">Sin categoría</option>
              <option v-for="categoria in categorias" :key="categoria.id" :value="categoria.id">
                {{ categoria.nombre }}
              </option>
            </select>
            <small v-if="categorias.length === 0" class="text-danger">
              No se han podido cargar las categorías
            </small>
          </div>
          
          <div class="mb-3">
            <label for="observaciones" class="form-label">Observaciones</label>
            <textarea
              class="form-control"
              id="observaciones"
              v-model="form.observaciones"
              rows="3"
              placeholder="Observaciones adicionales"
            ></textarea>
          </div>
          
          <div class="d-flex justify-content-between">
            <button type="button" class="btn btn-secondary" @click="$emit('cancelar')">
              Cancelar
            </button>
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              {{ editMode ? 'Actualizar' : 'Guardar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '../config/axios';
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'GastoPlanificadoForm',
  props: {
    gasto: {
      type: Object,
      default: null
    },
    editMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['guardar', 'cancelar'],
  setup(props, { emit }) {
    const store = useStore();
    const categorias = ref([]);
    const isSubmitting = ref(false);
    const mostrarFechaInicio = ref(false);
    
    // Función para validar fechas
    const isValidDate = (dateString) => {
      if (!dateString) return false;
      
      // Verificar formato YYYY-MM-DD
      const regex = /^\d{4}-\d{2}-\d{2}$/;
      if (!regex.test(dateString)) return false;
      
      // Verificar que sea una fecha válida
      const date = new Date(dateString);
      const timestamp = date.getTime();
      if (isNaN(timestamp)) return false;
      
      return date.toISOString().slice(0, 10) === dateString;
    };
    
    // Formulario
    const form = ref({
      concepto: '',
      cantidad: '',
      tipo_division: 'total',
      num_pagos: 2,
      fecha_inicio: '',
      periodicidad: 'mensual',
      categoria_id: '',
      observaciones: '',
      pagos_realizados: 0
    });

    const calcularCantidadTotal = () => {
      if (!form.value.cantidad) return 0;
      const cantidadNum = parseFloat(form.value.cantidad);
      if (isNaN(cantidadNum)) return 0;
      return form.value.tipo_division === 'mitad' ? cantidadNum / 2 : cantidadNum;
    };

    const cantidadTotalMostrar = computed(() => {
      const total = calcularCantidadTotal();
      return total ? `${total.toFixed(2)} €` : '0.00 €';
    });
    
    // Calcular cantidad por pago (sobre el importe a cargo del usuario)
    const cantidadPorPago = computed(() => {
      const total = calcularCantidadTotal();
      if (!total || !form.value.num_pagos) return 0;
      return total / parseInt(form.value.num_pagos);
    });
    
    // Cargar categorías
    const loadCategorias = async () => {
      try {
        console.log('Cargando categorías en GastoPlanificadoForm...');
        
        // Primero intentamos obtener las categorías del store
        let categoriasStore = store.getters.getCategorias;
        
        if (categoriasStore && categoriasStore.length > 0) {
          console.log('Usando categorías del store:', categoriasStore.length);
          categorias.value = categoriasStore;
        } else {
          // Si no hay categorías en el store, las cargamos desde la API
          console.log('No hay categorías en el store, cargando desde API...');
          try {
            await store.dispatch('fetchCategorias');
            categoriasStore = store.getters.getCategorias;
            
            if (categoriasStore && categoriasStore.length > 0) {
              console.log('Categorías cargadas desde el store:', categoriasStore.length);
              categorias.value = categoriasStore;
            } else {
              // Si aún no hay categorías, hacemos una llamada directa
              console.log('Intentando cargar categorías directamente...');
              const response = await axios.get('/api/categorias');
              console.log('Respuesta de categorías (directa):', response);
              categorias.value = response.data;
            }
          } catch (storeError) {
            console.error('Error al cargar categorías desde el store:', storeError);
            
            // Intento directo como fallback
            console.log('Intentando cargar categorías directamente como fallback...');
            const response = await axios.get('/api/categorias');
            console.log('Respuesta de categorías (fallback):', response);
            categorias.value = response.data;
          }
        }
        
        console.log('Categorías finalmente cargadas:', categorias.value);
      } catch (error) {
        console.error('Error al cargar categorías:', error);
        console.error('Detalles del error:', {
          mensaje: error.message,
          respuesta: error.response?.data,
          estado: error.response?.status,
          config: error.config
        });
      }
    };
    
    // Eliminar fecha de inicio
    const eliminarFechaInicio = () => {
      form.value.fecha_inicio = '';
      mostrarFechaInicio.value = false;
    };
    
    // Inicializar formulario con datos existentes si estamos en modo edición
    const initForm = () => {
      if (props.editMode && props.gasto) {
        form.value = {
          id: props.gasto.id,
          concepto: props.gasto.concepto,
          cantidad: props.gasto.cantidad != null && props.gasto.cantidad !== ''
            ? props.gasto.cantidad
            : props.gasto.cantidad_total,
          tipo_division: props.gasto.tipo_division || 'total',
          num_pagos: props.gasto.num_pagos,
          fecha_inicio: props.gasto.fecha_inicio,
          periodicidad: props.gasto.periodicidad,
          categoria_id: props.gasto.categoria_id || '',
          observaciones: props.gasto.observaciones || '',
          pagos_realizados: props.gasto.pagos_realizados || 0
        };
        
        // Si hay fecha de inicio, mostrar el campo
        if (props.gasto.fecha_inicio && props.gasto.fecha_inicio !== '') {
          mostrarFechaInicio.value = true;
        }
      }
    };
    
    // Enviar formulario
    const submitForm = async () => {
      isSubmitting.value = true;
      
      try {
        // Validar fecha de inicio si es necesario
        if (form.value.fecha_inicio && !isValidDate(form.value.fecha_inicio)) {
          alert('La fecha de inicio no es válida. Por favor, utilice el formato YYYY-MM-DD.');
          isSubmitting.value = false;
          return;
        }

        if (!form.value.cantidad || parseFloat(form.value.cantidad) <= 0) {
          alert('La cantidad debe ser mayor que 0');
          isSubmitting.value = false;
          return;
        }
        
        // Preparar datos para enviar
        const cantidadTotal = calcularCantidadTotal();
        const gastoData = {
          concepto: form.value.concepto,
          cantidad: parseFloat(form.value.cantidad),
          tipo_division: form.value.tipo_division,
          cantidad_total: cantidadTotal,
          num_pagos: parseInt(form.value.num_pagos),
          fecha_inicio: form.value.fecha_inicio || null,
          periodicidad: form.value.periodicidad,
          categoria_id: form.value.categoria_id || null,
          observaciones: form.value.observaciones || null
        };
        
        let response;
        
        if (props.editMode) {
          // Actualizar gasto existente
          response = await axios.put(`/api/gastos-planificados/${form.value.id}`, gastoData);
        } else {
          // Crear nuevo gasto
          response = await axios.post('/api/gastos-planificados', gastoData);
        }
        
        // Emitir evento de éxito
        emit('guardar', response.data);
        
      } catch (error) {
        console.error('Error al guardar gasto planificado:', error);
        alert('Error al guardar el gasto planificado. Por favor, inténtelo de nuevo.');
      } finally {
        isSubmitting.value = false;
      }
    };
    
    // Cargar datos al montar el componente
    onMounted(() => {
      console.log('Componente GastoPlanificadoForm montado');
      loadCategorias();
      initForm();
    });
    
    return {
      form,
      categorias,
      isSubmitting,
      cantidadPorPago,
      cantidadTotalMostrar,
      submitForm,
      isValidDate,
      mostrarFechaInicio,
      eliminarFechaInicio
    };
  }
};
</script>

<style scoped>
.gasto-planificado-form {
  max-width: 800px;
  margin: 0 auto;
}
</style> 